import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const CreateManualOrderModal = ({ onClose, onSuccess, setError, articles, initialData }) => {
  const [formData, setFormData] = useState({
    customer_name: initialData?.customer_name || "",
    customer_email: initialData?.customer_email || "",
    customer_phone: initialData?.customer_phone || "",
    customer_address: initialData?.customer_address || "",
    customer_city: initialData?.customer_city || "",
    customer_postal_code: initialData?.customer_postal_code || "",
    customer_country: initialData?.customer_country || "Italia",
    payment_method: initialData?.payment_method || "cash",
    total_amount: 0,
    shipping_cost: initialData?.shipping_cost || 0,
    notes: initialData?.notes || "",
    items: []
  });
  const [selectedArticles, setSelectedArticles] = useState(
    initialData?.items?.length > 0
      ? initialData.items.map(i => ({ article_id: i.article_id, quantity: i.quantity || 1 }))
      : [{ article_id: "", quantity: 1 }]
  );
  const [creating, setCreating] = useState(false);

  // Recalculate total when modal opens with initial data (mount-only by design;
  // selectedArticles is seeded above from initialData so we re-use that closure).
  // Adding `calculateTotal` or `selectedArticles` to deps would cause re-fires
  // on every state update and re-calc loops.
  useEffect(() => {
    if (initialData?.items?.length > 0) {
      calculateTotal(selectedArticles);
    }
  }, []);

  const addArticleRow = () => {
    setSelectedArticles([...selectedArticles, { article_id: "", quantity: 1 }]);
  };

  const removeArticleRow = (index) => {
    setSelectedArticles(selectedArticles.filter((_, i) => i !== index));
  };

  const updateArticle = (index, field, value) => {
    const updated = [...selectedArticles];
    updated[index][field] = value;
    setSelectedArticles(updated);
    
    // Recalculate total
    calculateTotal(updated);
  };

  const calculateTotal = (articlesData) => {
    let total = 0;
    articlesData.forEach(item => {
      if (item.article_id) {
        const article = articles.find(a => a.id === item.article_id);
        if (article) {
          total += article.price * item.quantity;
        }
      }
    });
    setFormData(prev => ({ ...prev, total_amount: total + parseFloat(prev.shipping_cost || 0) }));
  };

  const handleCreate = async () => {
    try {
      // Validate
      if (!formData.customer_name || !formData.customer_email || selectedArticles.length === 0) {
        setError("Compila tutti i campi obbligatori");
        setTimeout(() => setError(""), 3000);
        return;
      }

      // Build items array with title and price
      const orderItems = selectedArticles
        .filter(item => item.article_id)
        .map(item => {
          const article = articles.find(a => a.id === item.article_id);
          return {
            article_id: item.article_id,
            title: article.title,
            price: article.price,
            quantity: parseInt(item.quantity)
          };
        });

      if (orderItems.length === 0) {
        setError("Seleziona almeno un articolo");
        setTimeout(() => setError(""), 3000);
        return;
      }

      setCreating(true);
      const token = sessionStorage.getItem('access_token');
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/orders/manual`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          customer_info: {
            name: formData.customer_name,
            email: formData.customer_email,
            phone: formData.customer_phone,
            address: formData.customer_address,
            city: formData.customer_city,
            zipCode: formData.customer_postal_code,
            country: formData.customer_country,
            specificCountry: formData.customer_country,
            notes: formData.notes
          },
          items: orderItems,
          payment_method: formData.payment_method,
          total_amount: formData.total_amount,
          shipping_cost: parseFloat(formData.shipping_cost) || 0,
          status: "pending"
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Errore nella creazione');
      }

      onSuccess();
    } catch (error) {
      console.error("Error creating manual order:", error);
      setError(error.message || "Errore nella creazione dell'ordine");
      setTimeout(() => setError(""), 3000);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-white">{initialData ? "📋 Duplica Ordine" : "➕ Crea Ordine Manuale"}</h2>
            <Button variant="outline" onClick={onClose}>✕</Button>
          </div>

          <div className="space-y-6">
            {/* Customer Info */}
            <div className="p-4 bg-blue-50 rounded-lg">
              <h3 className="font-semibold text-lg mb-4">👤 Dati Cliente</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="customer_name">Nome Completo *</Label>
                  <Input
                    id="customer_name"
                    value={formData.customer_name}
                    onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
                    placeholder="es. Mario Rossi"
                  />
                </div>
                <div>
                  <Label htmlFor="customer_email">Email *</Label>
                  <Input
                    id="customer_email"
                    type="email"
                    value={formData.customer_email}
                    onChange={(e) => setFormData({...formData, customer_email: e.target.value})}
                    placeholder="es. mario.rossi@email.com"
                  />
                </div>
                <div>
                  <Label htmlFor="customer_phone">Telefono</Label>
                  <Input
                    id="customer_phone"
                    value={formData.customer_phone}
                    onChange={(e) => setFormData({...formData, customer_phone: e.target.value})}
                    placeholder="es. +39 123 456 7890"
                  />
                </div>
                <div>
                  <Label htmlFor="customer_city">Città</Label>
                  <Input
                    id="customer_city"
                    value={formData.customer_city}
                    onChange={(e) => setFormData({...formData, customer_city: e.target.value})}
                    placeholder="es. Roma"
                  />
                </div>
                <div>
                  <Label htmlFor="customer_address">Indirizzo</Label>
                  <Input
                    id="customer_address"
                    value={formData.customer_address}
                    onChange={(e) => setFormData({...formData, customer_address: e.target.value})}
                    placeholder="es. Via Roma 123"
                  />
                </div>
                <div>
                  <Label htmlFor="customer_postal_code">CAP</Label>
                  <Input
                    id="customer_postal_code"
                    value={formData.customer_postal_code}
                    onChange={(e) => setFormData({...formData, customer_postal_code: e.target.value})}
                    placeholder="es. 00100"
                  />
                </div>
                <div>
                  <Label htmlFor="customer_country">Paese</Label>
                  <Input
                    id="customer_country"
                    value={formData.customer_country}
                    onChange={(e) => setFormData({...formData, customer_country: e.target.value})}
                    placeholder="es. Italia, Bielorussia, Francia"
                    data-testid="manual-order-country"
                  />
                </div>
              </div>
            </div>

            {/* Articles */}
            <div className="p-4 bg-green-50 rounded-lg">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-lg">📦 Articoli</h3>
                <Button size="sm" onClick={addArticleRow} className="bg-green-600">
                  ➕ Aggiungi Articolo
                </Button>
              </div>
              {selectedArticles.map((item, index) => (
                <div key={`article-select-${item.article_id || index}`} className="flex gap-2 mb-2">
                  <select
                    value={item.article_id}
                    onChange={(e) => updateArticle(index, 'article_id', e.target.value)}
                    className="flex-1 px-3 py-2 border rounded"
                  >
                    <option value="">-- Seleziona Articolo --</option>
                    {articles.map((article) => (
                      <option key={article.id} value={article.id}>
                        {article.title} - €{article.price.toFixed(2)}
                      </option>
                    ))}
                  </select>
                  <Input
                    type="number"
                    min="1"
                    value={item.quantity}
                    onChange={(e) => updateArticle(index, 'quantity', e.target.value)}
                    className="w-20"
                    placeholder="Qtà"
                  />
                  {selectedArticles.length > 1 && (
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => removeArticleRow(index)}
                    >
                      🗑️
                    </Button>
                  )}
                </div>
              ))}
            </div>

            {/* Payment & Total */}
            <div className="p-4 bg-yellow-50 rounded-lg">
              <h3 className="font-semibold text-lg mb-4">💰 Pagamento</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="payment_method">Metodo Pagamento</Label>
                  <select
                    id="payment_method"
                    value={formData.payment_method}
                    onChange={(e) => setFormData({...formData, payment_method: e.target.value})}
                    className="w-full px-3 py-2 border rounded"
                  >
                    <option value="cash">Contrassegno</option>
                    <option value="transfer">Bonifico</option>
                    <option value="stripe">Carta</option>
                    <option value="satispay">Satispay</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="shipping_cost">Costo Spedizione (€)</Label>
                  <Input
                    id="shipping_cost"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.shipping_cost}
                    onChange={(e) => {
                      setFormData({...formData, shipping_cost: e.target.value});
                      calculateTotal(selectedArticles);
                    }}
                  />
                </div>
                <div>
                  <Label>Totale</Label>
                  <div className="text-2xl font-bold text-green-600">
                    €{formData.total_amount.toFixed(2)}
                  </div>
                </div>
              </div>
              <div className="mt-4">
                <Label htmlFor="notes">Note Ordine</Label>
                <textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="w-full px-3 py-2 border rounded"
                  rows={2}
                  placeholder="Note aggiuntive..."
                />
              </div>
            </div>
          </div>

          <div className="flex space-x-3 mt-6">
            <Button
              onClick={handleCreate}
              disabled={creating}
              className="flex-1 bg-green-600 hover:bg-green-700"
            >
              {creating ? "Creazione..." : "✅ Crea Ordine"}
            </Button>
            <Button
              variant="outline"
              onClick={onClose}
              disabled={creating}
              className="flex-1"
            >
              Annulla
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
